import ResourceSvc from '@/services/resource.svc.js';

import ClubDetailsSection from '@/components/user/dashboard/pages/clubs/register-club/sections/club-details-section/club-details-section.vue';
import JuniorMembersSection from '@/components/user/dashboard/pages/clubs/register-club/sections/junior-members-section/junior-members-section.vue';
import OfficersSection from '@/components/user/dashboard/pages/clubs/register-club/sections/officers-section/officers-section.vue';
import BankDetailsSection from '@/components/user/dashboard/pages/clubs/register-club/sections/bank-details-section/bank-details-section.vue';
import MembershipTypesSection from '@/components/user/dashboard/pages/clubs/register-club/sections/membership-types-section/membership-types-section.vue';
import AdminDocumentsSection from './sections/admin-documents-section/admin-documents-section.vue';
import AdminClubAdminsSection from './sections/admin-club-admins-section/admin-club-admins-section.vue';
import AdminClubMembersSection from './sections/admin-club-members-section/admin-club-members-section.vue';
import AdminClubPaymentsSection from './sections/admin-club-payments-section/admin-club-payments-section.vue';

import {
    IRISH_COUNTIES,
    createInitialClubState,
    createInitialValidationState,
    createEmptyOfficer,
    normalizeAdditionalOfficer,
    formatCountyOptions,
    parseIntOrNull,
    buildMembershipTypeRemovalWarning
} from '@/components/user/dashboard/pages/clubs/register-club/constants/club-registration-constants.js';
import ConfirmDialog from '@/components/shared/confirm-dialog/confirm-dialog.vue';

const resourceService = new ResourceSvc();

const ADMIN_SECTIONS = [
    { id: 'details', label: 'Club Details' },
    { id: 'junior', label: 'Junior Members' },
    { id: 'officers', label: 'Officers' },
    { id: 'bank', label: 'Bank Details' },
    { id: 'membership', label: 'Membership Types' },
    { id: 'documents', label: 'Documents & Settings' },
    { id: 'members', label: 'Club Members' },
    { id: 'payments', label: 'Payments' },
    { id: 'admins', label: 'Club Admins' }
];

const ADMIN_SECTIONS_CREATE = [
    { id: 'details', label: 'Club Details' },
    { id: 'junior', label: 'Junior Members' },
    { id: 'officers', label: 'Officers' },
    { id: 'bank', label: 'Bank Details' },
    { id: 'membership', label: 'Membership Types' },
    { id: 'members', label: 'Club Members' },
    { id: 'admins', label: 'Club Admins' }
];

const CLUB_STATUSES = [
    { label: 'Active', value: 1 },
    { label: 'Inactive', value: 2 },
    { label: 'Not Paid', value: 4 },
    { label: 'Historical', value: 5 },
    { label: 'Review', value: 6 }
];

let tempIdCounter = 0;
function nextTempId() {
    return -(++tempIdCounter);
}

export default {
    name: 'AdminClubForm',

    components: {
        'club-details-section': ClubDetailsSection,
        'junior-members-section': JuniorMembersSection,
        'officers-section': OfficersSection,
        'bank-details-section': BankDetailsSection,
        'membership-types-section': MembershipTypesSection,
        'admin-documents-section': AdminDocumentsSection,
        'admin-club-admins-section': AdminClubAdminsSection,
        'admin-club-members-section': AdminClubMembersSection,
        'admin-club-payments-section': AdminClubPaymentsSection,
        'confirm-dialog': ConfirmDialog
    },

    computed: {
        isCreateMode() {
            return this.$route.params.id === 'new';
        },

        isRenewalOptInVisible() {
            return !this.isCreateMode && this.club.isRenewalOptInOffered === true;
        }
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            errorMessage: null,
            activeSection: 'details',

            sections: ADMIN_SECTIONS,
            statuses: CLUB_STATUSES,
            clubStatus: 1,
            clubName: '',
            clubYear: '',
            availableYears: [],
            selectedCopyId: null,

            initData: {
                countyOptions: [],
                availableMembershipTypes: []
            },

            assignedMembershipTypes: [],
            selectedMembershipTypeId: null,
            selectedMembershipTypePrice: null,
            newMembershipTypeName: '',
            requestedMembershipTypes: [],
            resetRequestedMembershipTypes: false,

            acknowledgedMembershipTypeRemovals: false,
            isRemovalWarningVisible: false,
            removalWarning: null,
            pendingRemovalAction: null,

            documents: {},

            clubAdmins: [],
            clubAdminIds: [],

            clubMembers: [],
            clubMemberIds: [],

            feePaymentType: '',
            affiliationFees: [],

            club: createInitialClubState(),
            validation: createInitialValidationState()
        };
    },

    methods: {
        showError(message) {
            this.$showError(message || 'Something went wrong. Incorrect request!');
        },

        showSuccess(message) {
            this.$showSuccess(message || 'Operation success!');
        },

        goBack() {
            this.$router.push('/admin/manage-clubs');
        },

        parseAdminFromResponse(data) {
            return {
                id: data['Id'],
                tiNumber: data['TI Number'],
                fullName: ((data['First Name'] || '') + ' ' + (data['Last Name'] || '')).trim(),
                email: data['Email'] || ''
            };
        },

        async loadDefaultAdmins() {
            let tiNumbers = [];
            try {
                const configRes = await resourceService.getDefaultAdminTiNumbers();
                if (configRes && configRes.body && configRes.body.isSuccess && configRes.body.outputData) {
                    tiNumbers = configRes.body.outputData;
                }
            } catch (e) {
                this.showError('Failed to load default admin TI numbers');
                return;
            }

            if (!tiNumbers.length) return;

            const results = await Promise.all(
                tiNumbers.map(ti =>
                    resourceService.getMemberByLoginOrTiID(ti)
                        .then(res => ({ status: 'fulfilled', value: res }))
                        .catch(err => ({ status: 'rejected', reason: err }))
                )
            );

            const admins = [];
            results.forEach((result, i) => {
                if (result.status === 'fulfilled') {
                    const res = result.value;
                    if (res && res.body && res.body.isSuccess && res.body.outputData) {
                        admins.push(this.parseAdminFromResponse(res.body.outputData));
                    } else {
                        this.showError('Failed to load default admin with TI Number ' + tiNumbers[i]);
                    }
                } else {
                    this.showError('Error loading default admin with TI Number ' + tiNumbers[i]);
                }
            });

            this.clubAdmins = admins;
            this.clubAdminIds = admins.map(a => a.id);
        },

        async loadData() {
            try {
                this.isLoading = true;
                this.errorMessage = null;

                if (this.isCreateMode) {
                    this.sections = ADMIN_SECTIONS_CREATE;
                    this.clubStatus = 1;
                    this.clubName = '';
                    this.clubYear = new Date().getFullYear();
                    this.club = createInitialClubState();
                    this.club.year = new Date().getFullYear();

                    const typesRes = await resourceService.getAllMembershipTypes();
                    if (typesRes && typesRes.body && typesRes.body.isSuccess) {
                        const types = typesRes.body.outputData || [];
                        this.initData.availableMembershipTypes = types.map(t => ({
                            label: t.clubMembershipTypeLabel || t.clubMembershipType,
                            value: t.clubMembershipTypeId
                        }));
                    }

                    this.initData.countyOptions = formatCountyOptions(IRISH_COUNTIES);

                    await this.loadDefaultAdmins();
                    return;
                }

                this.sections = ADMIN_SECTIONS;
                const clubId = this.$route.params.id;
                const year = this.$route.query && this.$route.query.year ? this.$route.query.year : null;

                const [clubRes, typesRes] = await Promise.all([
                    resourceService.getAdminClubDetail(clubId, year),
                    resourceService.getAllMembershipTypes()
                ]);

                if (!clubRes || !clubRes.body || !clubRes.body.isSuccess) {
                    this.errorMessage = (clubRes && clubRes.body && clubRes.body.message) || 'Failed to load club details';
                    return;
                }

                const dto = clubRes.body.outputData;
                this.mapDtoToState(dto);

                if (typesRes && typesRes.body && typesRes.body.isSuccess) {
                    const types = typesRes.body.outputData || [];
                    this.initData.availableMembershipTypes = types.map(t => ({
                        label: t.clubMembershipTypeLabel || t.clubMembershipType,
                        value: t.clubMembershipTypeId
                    }));
                }

                this.initData.countyOptions = formatCountyOptions(IRISH_COUNTIES);
            } catch (error) {
                this.errorMessage = error.message || 'Failed to load data';
            } finally {
                if (!this._isDestroyed) this.isLoading = false;
            }
        },

        mapDtoToState(dto) {
            this.clubStatus = dto.status || 1;
            this.clubName = dto.name || '';
            this.clubYear = dto.year || '';
            this.availableYears = dto.availableYears || [];

            this.club = {
                ...createInitialClubState(),
                id: dto.id,
                clubYearDataId: dto.clubYearDataId || null,
                name: dto.name || '',
                description: dto.description || '',
                membersLimit: dto.membersLimit || null,
                year: dto.year || new Date().getFullYear(),
                clubEmail: dto.clubEmail || '',
                clubWebsite: dto.clubWebsite || '',
                town: dto.town || '',
                county: dto.county || '',
                noOfMembersLastYear: parseIntOrNull(dto.noOfMembersLastYear),
                yearFormed: parseIntOrNull(dto.yearFormed),
                facebook: dto.facebook || '',
                twitter: dto.twitter || '',
                hasJuniorMembers: dto.hasJuniorMembers === true,
                allowClubRenewalOptIn: dto.allowClubRenewalOptIn === true
                    ? 'Yes'
                    : (dto.allowClubRenewalOptIn === false ? 'No' : null),
                isRenewalOptInOffered: dto.isRenewalOptInOffered === true,
                juniorMembersLimit: dto.juniorMembersLimit,
                juniorAgeGroups: dto.juniorAgeGroups || [],
                juniorAgeGroupsAdditional: dto.juniorAgeGroupsAdditional || '',
                chairperson: dto.chairperson || createEmptyOfficer(),
                treasurer: dto.treasurer || createEmptyOfficer(),
                secretary: dto.secretary || createEmptyOfficer(),
                membershipOfficer: dto.membershipOfficer || createEmptyOfficer(),
                womensOfficer: dto.womensOfficer || createEmptyOfficer(),
                safeguardingOfficer: dto.safeguardingOfficer || createEmptyOfficer(),
                inclusionDiversityOfficer: dto.inclusionDiversityOfficer || createEmptyOfficer(),
                designatedLiaisonPerson: dto.designatedLiaisonPerson || createEmptyOfficer(),
                additionalOfficers: (dto.additionalOfficers || []).map(normalizeAdditionalOfficer),
                bankAccountName: dto.bankAccountName || '',
                bankName: dto.bankName || '',
                bankAddress: dto.bankAddress || '',
                iban: dto.iban || '',
                bicNumber: dto.bicNumber || '',
                safeguardStatementLink: dto.safeguardStatementLink || '',
                safeguardStatementDescription: dto.safeguardStatementDescription || '',
                acceptTerms: dto.acceptTerms === true,
                notificationState: dto.notificationState === true,
                notificationEmailAddress: dto.notificationEmailAddress || '',
                feePaymentType: dto.feePaymentType || ''
            };

            this.assignedMembershipTypes = (dto.assignedMembershipTypes || []).map(t => ({
                id: t.id,
                clubMembershipTypeId: t.clubMembershipTypeId,
                name: t.name,
                price: t.price
            }));

            this.requestedMembershipTypes = dto.requestedMembershipTypes || [];

            this.documents = dto.documents || {};

            this.clubAdmins = (dto.clubAdmins || []).map(a => ({
                id: a.id,
                tiNumber: a.tiNumber,
                fullName: a.fullName,
                email: a.email
            }));
            this.clubAdminIds = (dto.clubAdmins || []).map(a => a.id);

            this.clubMembers = (dto.clubMembers || []).map(m => ({
                id: m.id,
                tiNumber: m.tiNumber,
                fullName: m.fullName,
                email: m.email,
                gender: m.gender,
                dateOfBirth: m.dateOfBirth,
                address: m.address,
                phone: m.phone,
                membershipType: m.membershipType,
                paymentStatus: m.paymentStatus,
                isAutoRenewalEnabled: m.isAutoRenewalEnabled
            }));
            this.clubMemberIds = (dto.clubMembers || []).map(m => m.id);

            this.feePaymentType = dto.feePaymentType || '';
            this.affiliationFees = (dto.affiliationFees || []).map(f => ({
                id: f.id,
                paymentDate: f.paymentDate,
                purchaseType: f.purchaseType,
                description: f.description,
                price: f.price
            }));
        },

        buildPayload() {
            const { club } = this;
            return {
                id: club.id,
                clubYearDataId: club.clubYearDataId,
                name: club.name,
                description: club.description || '',
                membersLimit: club.membersLimit,
                year: club.year,
                status: this.clubStatus,
                clubEmail: club.clubEmail || '',
                clubWebsite: club.clubWebsite || '',
                town: club.town || '',
                county: club.county || '',
                noOfMembersLastYear: club.noOfMembersLastYear,
                yearFormed: club.yearFormed,
                facebook: club.facebook || '',
                twitter: club.twitter || '',
                hasJuniorMembers: club.hasJuniorMembers === true,
                allowClubRenewalOptIn: this.isRenewalOptInVisible
                    ? (club.allowClubRenewalOptIn === 'Yes'
                        ? true
                        : (club.allowClubRenewalOptIn === 'No' ? false : null))
                    : null,
                juniorMembersLimit: club.juniorMembersLimit,
                juniorAgeGroups: club.juniorAgeGroups || [],
                juniorAgeGroupsAdditional: club.juniorAgeGroupsAdditional || '',
                chairperson: club.chairperson || createEmptyOfficer(),
                treasurer: club.treasurer || createEmptyOfficer(),
                secretary: club.secretary || createEmptyOfficer(),
                membershipOfficer: club.membershipOfficer || createEmptyOfficer(),
                womensOfficer: club.womensOfficer || createEmptyOfficer(),
                safeguardingOfficer: club.safeguardingOfficer || createEmptyOfficer(),
                inclusionDiversityOfficer: club.inclusionDiversityOfficer || createEmptyOfficer(),
                designatedLiaisonPerson: club.designatedLiaisonPerson || createEmptyOfficer(),
                additionalOfficers: club.additionalOfficers || [],
                bankAccountName: club.bankAccountName || '',
                bankName: club.bankName || '',
                bankAddress: club.bankAddress || '',
                iban: club.iban || '',
                bicNumber: club.bicNumber || '',
                safeguardStatementLink: club.safeguardStatementLink || '',
                safeguardStatementDescription: club.safeguardStatementDescription || '',
                acceptTerms: club.acceptTerms === true,
                notificationState: club.notificationState === true,
                notificationEmailAddress: club.notificationEmailAddress || '',
                feePaymentType: this.feePaymentType || club.feePaymentType || '',
                assignedMembershipTypes: this.assignedMembershipTypes.map(t => ({
                    id: t.id || 0,
                    clubMembershipTypeId: t.clubMembershipTypeId,
                    name: t.name || '',
                    price: Number(t.price) || 0
                })),
                resetRequestedMembershipTypes: this.resetRequestedMembershipTypes,
                acknowledgedMembershipTypeRemovals: this.acknowledgedMembershipTypeRemovals === true,
                clubAdminIds: this.clubAdminIds,
                clubMemberIds: this.clubMemberIds
            };
        },

        async saveClub() {
            try {
                this.isSaving = true;

                if (this.isCreateMode) {
                    const payload = this.buildPayload();
                    payload.id = 0;
                    const response = await resourceService.createAdminClub(payload);

                    if (response && response.body && response.body.isSuccess) {
                        const newClubId = response.body.outputData.clubId;
                        this.showSuccess('Club created successfully');
                        this.$router.replace({
                            path: '/admin/club/' + newClubId,
                            query: payload.year ? { year: payload.year } : {}
                        });
                    } else {
                        const msg = (response && response.body && response.body.message) || 'Failed to create club';
                        this.showError(msg);
                    }
                    return;
                }

                const response = await resourceService.saveAdminClub(this.buildPayload());

                if (response && response.body && response.body.isSuccess) {
                    const result = response.body.outputData;

                    if (result && result.status === 'ConfirmationRequired') {
                        this.confirmMembershipTypeRemovals(result.removedMembershipTypes, 'save');
                        return;
                    }

                    this.showSuccess(response.body.successMessage || 'Club saved successfully');
                    await this.loadData();
                } else {
                    const msg = (response && response.body && response.body.message) || 'Failed to save club';
                    this.showError(msg);
                }
            } catch (error) {
                this.showError(error.message || 'An error occurred');
            } finally {
                this.isSaving = false;
            }
        },

        assignMembershipType() {
            if (!this.selectedMembershipTypeId) {
                this.showError('Please select a membership type');
                return;
            }

            if (this.selectedMembershipTypePrice === null || this.selectedMembershipTypePrice < 0) {
                this.showError('Please enter a valid price');
                return;
            }

            const selectedType = this.initData.availableMembershipTypes.find(
                t => t.value === this.selectedMembershipTypeId
            );

            if (!selectedType) return;

            this.assignedMembershipTypes.push({
                id: nextTempId(),
                clubMembershipTypeId: selectedType.value,
                name: selectedType.label,
                price: this.selectedMembershipTypePrice
            });

            this.selectedMembershipTypeId = null;
            this.selectedMembershipTypePrice = null;
        },

        removeMembershipType(index) {
            this.assignedMembershipTypes.splice(index, 1);
        },

        addNewMembershipType() {
            const trimmedName = this.newMembershipTypeName ? this.newMembershipTypeName.trim() : '';

            if (!trimmedName) {
                this.showError('Please enter a membership type name');
                return;
            }

            const nameExists = this.initData.availableMembershipTypes.some(
                t => t.label.toLowerCase() === trimmedName.toLowerCase()
            );

            if (nameExists) {
                this.showError('A membership type with this name already exists');
                return;
            }

            const tempTypeId = nextTempId();
            this.initData.availableMembershipTypes.push({
                label: trimmedName,
                value: tempTypeId
            });

            this.selectedMembershipTypeId = tempTypeId;
            this.newMembershipTypeName = '';
            this.showSuccess('New membership type created. Set the price and click Assign.');
        },

        acceptRequestedMembership(index) {
            const requested = this.requestedMembershipTypes[index];
            if (!requested) return;

            if (requested.clubMembershipTypeId > 0) {
                const alreadyAssigned = this.assignedMembershipTypes.some(
                    t => t.clubMembershipTypeId === requested.clubMembershipTypeId
                );
                if (!alreadyAssigned) {
                    this.assignedMembershipTypes.push({
                        id: nextTempId(),
                        clubMembershipTypeId: requested.clubMembershipTypeId,
                        name: requested.name,
                        price: requested.price
                    });
                }
            } else {
                const tempTypeId = nextTempId();
                this.initData.availableMembershipTypes.push({
                    label: requested.name,
                    value: tempTypeId
                });

                this.assignedMembershipTypes.push({
                    id: nextTempId(),
                    clubMembershipTypeId: tempTypeId,
                    name: requested.name,
                    price: requested.price
                });
            }

            this.requestedMembershipTypes.splice(index, 1);

            if (this.requestedMembershipTypes.length === 0) {
                this.resetRequestedMembershipTypes = true;
            }

            this.showSuccess('Membership type accepted. Click "Save Changes" to persist.');
        },

        resetRequestedMembership() {
            this.requestedMembershipTypes = [];
            this.resetRequestedMembershipTypes = true;
            this.showSuccess('Requested membership types reset. Click "Save Changes" to persist.');
        },

        async requestDeleteClub(acknowledged) {
            try {
                const response = await resourceService.deleteAdminClub(this.club.id, this.club.year, acknowledged);
                const body = response && response.body;

                if (body && body.isSuccess && body.outputData
                    && body.outputData.status === 'ConfirmationRequired') {
                    const result = body.outputData;
                    const memberText = result.memberCount > 0
                        ? result.memberCount + ' member(s) hold a ' + result.year
                            + ' membership of this club. Removing it will end those memberships.'
                        : '';

                    if (result.removedMembershipTypes && result.removedMembershipTypes.length) {
                        this.confirmMembershipTypeRemovals(result.removedMembershipTypes, 'delete', memberText);
                        return;
                    }

                    this.confirmDeleteClub(true, memberText);
                    return;
                }

                if (body && body.isSuccess) {
                    this.showSuccess('Club deleted successfully');
                    this.goBack();
                } else {
                    const msg = (body && body.message) || 'Failed to delete club';
                    this.showError(msg);
                }
            } catch (error) {
                this.showError(error.message || 'An error occurred while deleting the club');
            }
        },
        confirmDeleteClub(acknowledged, extraText) {
            const question = 'Do you really want to delete the ' + this.club.year
                + ' affiliation for club "' + this.clubName + '"?';

            this.$modal({
                caption: 'Delete Club',
                text: extraText ? extraText + ' ' + question : question,
                confirmText: 'Yes',
                cancelText: 'No',
                cancelFn: () => {},
                confirmFn: async () => {
                    await this.requestDeleteClub(acknowledged);
                }
            });
        },
        async changeYear(year) {
            if (!year || year == this.club.year) {
                return;
            }

            this.$router.replace({
                path: this.$route.path,
                query: Object.assign({}, this.$route.query, { year: year })
            });

            await this.loadData();
        },
        deleteClub() {
            this.confirmDeleteClub(false);
        },

        confirmMembershipTypeRemovals(removedMembershipTypes, action, extraNote) {
            const warning = buildMembershipTypeRemovalWarning(removedMembershipTypes);

            if (extraNote) {
                warning.note = extraNote + ' ' + warning.note;
            }

            this.removalWarning = warning;
            this.pendingRemovalAction = action;
            this.isRemovalWarningVisible = true;
        },

        cancelMembershipTypeRemovals() {
            this.isRemovalWarningVisible = false;
            this.removalWarning = null;
            this.pendingRemovalAction = null;
        },

        async acceptMembershipTypeRemovals() {
            const action = this.pendingRemovalAction;
            this.cancelMembershipTypeRemovals();

            if (action === 'delete') {
                await this.requestDeleteClub(true);
                return;
            }

            this.acknowledgedMembershipTypeRemovals = true;
            await this.saveClub();
        }
    },

    watch: {
        assignedMembershipTypes() {
            this.acknowledgedMembershipTypeRemovals = false;
        }
    },

    beforeDestroy() {
        this._isDestroyed = true;
    },

    mounted() {
        this.loadData();
    }
};



// WEBPACK FOOTER //
// ./src/components/admin/club/club-detail/admin-club-form.js